"use client";

import * as React from "react";
import clsx from "clsx";
import { CheckIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";
import {
  BarsArrowDownIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";

import { cn } from "#/lib/utils";
import { useTStream } from "#/hooks/use-t-stream";
import { Button } from "#/ui/button";
import MultiSelect from "#/ui/multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "#/ui/popover";

export interface LocationSelectOption {
  value: string;
  label: string;
}

export interface LocationsFilterTab {
  value: string;
  label: string;
}

/**
 * Badge-style multi-select for locations. Purely props-driven — the caller
 * supplies `options` (e.g. from `useLocationOptions()`) and owns selection
 * state. `filterTabs` generalizes the "All Locations / DSP Connected Only"
 * toggle into a reusable variant: pass a different `options` set per active
 * tab from the parent.
 */
export const LocationsMultiSelect = ({
  options,
  selectedLocationIds,
  onChange,
  enableSelectAll = false,
  title = "",
  className = "",
  classNameTitle = "",
  onCloseAutoFocus = () => {},
  disabled = false,
  placeholder,
  selectedBadgeVariant,
  filterTabs,
  activeFilterTab,
  onFilterTabChange,
}: {
  options: LocationSelectOption[];
  selectedLocationIds: string[];
  onChange: (ids: string[]) => void;
  enableSelectAll?: boolean;
  title?: string;
  className?: string;
  classNameTitle?: string;
  onCloseAutoFocus?: () => void;
  disabled?: boolean;
  placeholder?: string;
  selectedBadgeVariant?: React.ComponentProps<
    typeof MultiSelect
  >["selectedBadgeVariant"];
  filterTabs?: LocationsFilterTab[];
  activeFilterTab?: string;
  onFilterTabChange?: (value: string) => void;
}) => {
  const { t } = useTStream("common");
  const resolvedPlaceholder = placeholder || t("Select locations");
  const selectedOptions = React.useMemo(
    () => options.filter((o) => selectedLocationIds?.includes(o.value)),
    [options, selectedLocationIds],
  );

  const buttonText = React.useMemo(() => {
    if (selectedOptions.length === 0) return resolvedPlaceholder;
    if (selectedOptions.length > 1)
      return t("locationsSelectedCount", { count: selectedOptions.length });
    return selectedOptions[0]?.label;
  }, [selectedOptions, resolvedPlaceholder, t]);

  const onFilter = React.useCallback(
    (values: { value: string; label: string }[]) =>
      onChange(values?.map((v) => v?.value) ?? []),
    [onChange],
  );

  return (
    <MultiSelect
      defaultValues={selectedLocationIds}
      onChange={onFilter}
      title={title || buttonText}
      disableSelect={disabled}
      disabled={disabled}
      classNameTitle={classNameTitle || "bg-background"}
      hideCloseButton
      className={className}
      enableSelectAll={enableSelectAll}
      options={options}
      onCloseAutoFocus={onCloseAutoFocus}
      selectedBadgeVariant={selectedBadgeVariant}
    >
      {filterTabs?.length > 0 && (
        <div className="inline-flex mb-2 w-full bg-background text-muted-foreground border-b">
          {filterTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => onFilterTabChange?.(tab.value)}
              className={cn(
                "flex-1 inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                activeFilterTab === tab.value
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "hover:bg-muted-foreground/10",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </MultiSelect>
  );
};

/**
 * Compact popover list for single/multi location selection with a
 * button-style trigger (distinct visual from `LocationsMultiSelect`'s badge
 * style — kept for existing call sites that expect this look).
 */
export const LocationsSingleSelect = ({
  options,
  selectedLocationIds,
  onChange,
  multiSelect = true,
  title = "",
  titleClassName = "",
  placeholder,
}: {
  options: LocationSelectOption[];
  selectedLocationIds: string[];
  onChange: (ids: string[]) => void;
  multiSelect?: boolean;
  title?: string;
  titleClassName?: string;
  placeholder?: string;
}) => {
  const { t } = useTStream("common");
  const resolvedPlaceholder = placeholder || t("Select locations");
  const [open, setOpen] = React.useState(false);

  const selectedOptions = React.useMemo(
    () => options.filter((o) => selectedLocationIds?.includes(o.value)),
    [options, selectedLocationIds],
  );

  const buttonText = React.useMemo(() => {
    if (selectedOptions.length === 0) return resolvedPlaceholder;
    if (selectedOptions.length > 1)
      return t("locationsSelectedCount", { count: selectedOptions.length });
    return selectedOptions[0]?.label;
  }, [selectedOptions, resolvedPlaceholder, t]);

  const onSelect = React.useCallback(
    (value: string) => {
      if (multiSelect) {
        if (selectedLocationIds.includes(value)) {
          onChange(selectedLocationIds.filter((id) => id !== value));
        } else {
          onChange([...selectedLocationIds, value]);
        }
      } else {
        onChange([value]);
        setOpen(false);
      }
    },
    [multiSelect, onChange, selectedLocationIds],
  );

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger className="relative" asChild>
          <div>
            {title && (
              <p
                className={twMerge(
                  "text-400 text-muted text-sm absolute font-circular-std bg-background font-500 left-[1rem] top-[-0.75rem] z-10",
                  titleClassName,
                )}
              >
                {title}
              </p>
            )}
            <Button className="px-2" size="md" variant="outline-muted">
              <BarsArrowDownIcon className="h-4 w-4 text-muted" />
              <p className="px-2">{buttonText}</p>
              {open ? (
                <ChevronUpIcon className="h-4 w-4 text-muted" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 text-muted" />
              )}
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent className="py-2">
          {options?.map((option, index) => (
            <div
              className={clsx("px-3 cursor-pointer border-border", {
                "border-b-[1px]": index !== options?.length - 1,
              })}
              key={option.value}
              onClick={() => onSelect(option.value)}
            >
              <div className="text-sm flex h-10 items-center justify-between text-foreground font-circular-regular">
                <p className="truncate">{option.label}</p>
                {selectedLocationIds?.includes?.(option.value) ? (
                  <CheckIcon className="h-4 w-4 ml-2 text-green-500" />
                ) : (
                  <div className="w-4 ml-2" />
                )}
              </div>
            </div>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
};
